var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { themeColorsDark } from '../../JuvareTheme/colors/themeColorsDark';
import { MenuIconButton } from '../../MenuIconButton';
import { useCommonWebTranslation } from '../../hooks';
/**
 * Renders a toolbar displaying search and action items in a responsive layout.
 *
 * @param {ToolbarProps} root0 - Props object.
 * @param {string} [root0.searchLabel] - Optional label for the search input. Defaults to the localized 'Search' string.
 * @param {() => void} [root0.onSearchOpen] - Optional handler triggered when the search icon is clicked.
 * @param {GlobalHeaderItem[]} [root0.headerItems] - Optional list of header items to render as icons or menus.
 * @param {GlobalHeaderItem} [root0.headerPrimaryItem] - Optional primary item rendered as a chip link.
 * @param {boolean} root0.smallDevice - Flag indicating if the layout is on a small screen.
 *
 * @returns {JSX.Element | null} Responsive toolbar element or `null` if no items are provided.
 */
export const Toolbar = ({ searchLabel, onSearchOpen, headerItems: items, smallDevice, headerPrimaryItem }) => {
    const { t } = useCommonWebTranslation();
    if (searchLabel == null) {
        searchLabel = t('common.search');
    }
    if (items && items.length > 0) {
        if (smallDevice) {
            const flattenedMenuItems = headerPrimaryItem ? [headerPrimaryItem] : [];
            for (const item of items) {
                if (item.menuItems) {
                    flattenedMenuItems.push(...item.menuItems);
                }
                else {
                    flattenedMenuItems.push(item);
                }
            }
            return (React.createElement(Box, { sx: {
                    whiteSpace: 'nowrap',
                }, "data-test": 'right-menu' },
                onSearchOpen && (React.createElement(Tooltip, { title: searchLabel },
                    React.createElement(IconButton, { size: 'large', "aria-label": searchLabel, onClick: onSearchOpen, sx: {
                            // Global App header is always dark.
                            color: themeColorsDark.icon.subtle,
                            '&:hover': {
                                backgroundColor: themeColorsDark.overlay.hover,
                            },
                        }, "data-test": 'search-button' },
                        React.createElement(SearchIcon, null)))),
                React.createElement(MenuIconButton, { icon: React.createElement(MoreHorizIcon, null), menuItems: flattenedMenuItems, iconSize: 'large', sx: {
                        // Global App header is always dark.
                        color: themeColorsDark.icon.subtle,
                        '&:hover': {
                            backgroundColor: themeColorsDark.overlay.hover,
                        },
                    }, "data-test": 'more-button' })));
        }
        else {
            return (React.createElement(Box, { sx: { whiteSpace: 'nowrap' }, "data-test": 'right-menu' },
                headerPrimaryItem && (React.createElement(Chip, { clickable: true, component: 'a', "data-test": headerPrimaryItem['data-test'], href: headerPrimaryItem.href, icon: React.isValidElement(headerPrimaryItem.icon) ? headerPrimaryItem.icon : undefined, label: React.createElement(Typography, { color: themeColorsDark.text.subtle, variant: 'body2' }, headerPrimaryItem.label), onClick: headerPrimaryItem.onClick, sx: {
                        borderColor: themeColorsDark.border.main,
                        ml: 1,
                        padding: '6px',
                        ['& .MuiChip-icon']: {
                            // Global App header is always dark.
                            color: themeColorsDark.icon.subtle,
                        },
                        ':focus': {
                            borderColor: themeColorsDark.primary.base,
                        },
                    }, variant: 'outlined' })),
                onSearchOpen && (React.createElement(Tooltip, { title: searchLabel },
                    React.createElement(IconButton, { size: 'large', "aria-label": searchLabel, onClick: onSearchOpen, sx: {
                            // Global App header is always dark.
                            color: themeColorsDark.icon.subtle,
                            '&:hover': {
                                backgroundColor: themeColorsDark.overlay.hover,
                            },
                        }, "data-test": 'search-button' },
                        React.createElement(SearchIcon, null)))),
                items.map((_a) => {
                    var { onClick } = _a, item = __rest(_a, ["onClick"]);
                    return (React.createElement(React.Fragment, { key: item.label }, item.menuItems && item.menuItems.length > 0 ? (React.createElement(MenuIconButton, { icon: item.icon, menuItems: item.menuItems, iconSize: 'large', sx: {
                            // Global App header is always dark.
                            color: themeColorsDark.icon.subtle,
                            '&:hover': {
                                backgroundColor: themeColorsDark.overlay.hover,
                            },
                        }, "aria-label": item.label, "data-test": item['data-test'] })) : (React.createElement(Tooltip, { title: item.label },
                        React.createElement(IconButton, { size: 'large', "aria-label": item.label, href: item.href, onClick: onClick
                                ? (event) => {
                                    event.preventDefault();
                                    onClick();
                                }
                                : undefined, sx: {
                                // Global App header is always dark.
                                color: themeColorsDark.icon.subtle,
                                '&:hover': {
                                    backgroundColor: themeColorsDark.overlay.hover,
                                },
                            }, "data-test": item['data-test'] }, item.icon)))));
                })));
        }
    }
    else {
        return null;
    }
};
